
import React from "react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Trash2, RefreshCw, Mail, User, Calendar, AlertCircle } from "lucide-react";
import { useManageFolderShares } from "./useManageFolderShares";
import { ManageFolderSharesDialogProps } from "./types";

export const ManageFolderSharesDialog: React.FC<ManageFolderSharesDialogProps> = ({
  isOpen,
  onOpenChange,
  folderId,
  folderName
}) => {
  const {
    shares,
    isLoading,
    error,
    isRemoving,
    handleRemoveShare,
    refreshShares
  } = useManageFolderShares(folderId);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Hantera delningar för "{folderName}"</DialogTitle>
        </DialogHeader>

        <div className="flex justify-between items-center my-4">
          <h3 className="text-sm font-medium">{shares.length} delningar</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshShares}
            disabled={isLoading}
            className="text-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Uppdatera
          </Button>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-center">
            <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
            <p className="text-red-700">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshShares} 
              className="mt-2"
            >
              Försök igen
            </Button>
          </div>
        ) : isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin h-8 w-8 border-4 border-gray-200 border-t-blue-600 rounded-full"></div>
            <p className="mt-2 text-gray-500">Hämtar delningar...</p>
          </div>
        ) : shares.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-md">
            <p className="text-slate-500 mb-2">Mappen är inte delad med någon ännu</p>
            <p className="text-xs text-slate-400">
              Använd "Dela" knappen för att dela mappen med andra användare
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Användare</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shares.map((share) => {
                const userName = share.user_profiles 
                  ? `${share.user_profiles.first_name || ''} ${share.user_profiles.last_name || ''}`.trim() 
                  : null;
                
                const formattedDate = format(new Date(share.created_at), 'yyyy-MM-dd');
                
                return (
                  <TableRow key={share.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <Mail className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                          <span className="text-sm">{share.shared_with_email}</span>
                        </div>
                        {userName && (
                          <div className="flex items-center mt-1">
                            <User className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                            <span className="text-xs text-slate-500">{userName}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Calendar className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                        <span className="text-sm">{formattedDate}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveShare(share.id)}
                        disabled={isRemoving === share.id}
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {isRemoving === share.id ? (
                          <div className="h-3.5 w-3.5 animate-spin border-2 border-current border-t-transparent rounded-full mr-1.5" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Ta bort
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Stäng
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
